import { createContext } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { api } from '../services/api.js';

interface AuthUser {
  _id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  // 051: phone is already returned by GET /users/me; avatar_url is resolved
  // server-side so the avatar survives a reload.
  phone?: string;
  avatar_url?: string | null;
  status: string;
  /**
   * 154 (#22): both already arrive from `GET /users/me`, which sends the whole user document —
   * only the declaration was missing. Two independent facts: the email link was clicked, and
   * staff reviewed the identity documents. Neither implies the other (see #20).
   */
  email_verified_at?: string | null;
  identity_verification?: {
    status: 'pending' | 'submitted' | 'approved' | 'rejected' | 'resubmitted';
  };
  /**
   * 330: the customer's campus, and its name for "Your campus", both from `GET /users/me`. Absent until
   * chosen; then only staff change it.
   */
  campus_location_id?: string | null;
  campus?: { _id: string; name: string; is_active: boolean } | null;
  /** 366: the masked identity number (never the number itself), and whether it must still be given. */
  identity_number?: { kind: 'mykad' | 'passport'; country: string; masked: string } | null;
  identity_number_required?: boolean;
}

/** 036: impersonation context stored alongside the impersonation token so the
 * banner can name both parties without another round-trip. */
export interface ImpersonationContext {
  impersonator: { id: string; name: string };
  target: { id: string; name: string };
  started_at?: string | null;
}

const IMP_TOKEN_KEY = 'impersonation_token';
const IMP_CONTEXT_KEY = 'impersonation';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  isImpersonating: boolean;
  impersonation: ImpersonationContext | null;
}

interface AuthContextValue extends AuthState {
  login(email: string, password: string): Promise<void>;
  logout(): void;
  setTokens(accessToken: string, refreshToken: string): Promise<void>;
  refreshUser(): Promise<void>;
  enterImpersonation(token: string, context: ImpersonationContext): Promise<void>;
  exitImpersonation(): Promise<void>;
}

function readStoredImpersonation(): ImpersonationContext | null {
  const raw = localStorage.getItem(IMP_CONTEXT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationContext;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  loading: true,
  isImpersonating: false,
  impersonation: null,
  login: async () => {},
  logout: () => {},
  setTokens: async () => {},
  refreshUser: async () => {},
  enterImpersonation: async () => {},
  exitImpersonation: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: preact.ComponentChildren }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    loading: true,
    isImpersonating: false,
    impersonation: null,
  });

  useEffect(() => {
    // 036: an impersonation token takes precedence — the app acts as the target.
    const impToken = localStorage.getItem(IMP_TOKEN_KEY);
    const stored = impToken ?? localStorage.getItem('access_token');
    if (!stored) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    api
      .get<AuthUser>('/users/me')
      .then((user) =>
        setState({
          user,
          accessToken: stored,
          isAuthenticated: true,
          loading: false,
          isImpersonating: Boolean(impToken),
          impersonation: impToken ? readStoredImpersonation() : null,
        }),
      )
      .catch(() => {
        localStorage.removeItem(impToken ? IMP_TOKEN_KEY : 'access_token');
        setState({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          loading: false,
          isImpersonating: false,
          impersonation: null,
        });
      });
  }, []);

  async function setTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    const user = await api.get<AuthUser>('/users/me');
    setState((s) => ({ ...s, user, accessToken, isAuthenticated: true, loading: false }));
  }

  // 036 US1 — adopt an impersonation session: store the distinct token + context
  // (preserving any real customer session), then load the target's identity.
  async function enterImpersonation(token: string, context: ImpersonationContext) {
    localStorage.setItem(IMP_TOKEN_KEY, token);
    localStorage.setItem(IMP_CONTEXT_KEY, JSON.stringify(context));
    const user = await api.get<AuthUser>('/users/me');
    setState((s) => ({
      ...s,
      user,
      accessToken: token,
      isAuthenticated: true,
      loading: false,
      isImpersonating: true,
      impersonation: context,
    }));
  }

  // 036 US2 — end impersonation: tell the server to stop, drop the impersonation
  // token/context, and restore any prior real session (or sign out).
  async function exitImpersonation() {
    await api.post('/auth/impersonate/stop').catch(() => {});
    localStorage.removeItem(IMP_TOKEN_KEY);
    localStorage.removeItem(IMP_CONTEXT_KEY);
    const restored = localStorage.getItem('access_token');
    if (!restored) {
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        loading: false,
        isImpersonating: false,
        impersonation: null,
      });
      return;
    }
    const user = await api.get<AuthUser>('/users/me').catch(() => null);
    setState({
      user,
      accessToken: user ? restored : null,
      isAuthenticated: Boolean(user),
      loading: false,
      isImpersonating: false,
      impersonation: null,
    });
  }

  // 051: re-fetch the current user so profile/avatar edits reflect app-wide
  // without a full reload (FR-011).
  async function refreshUser() {
    const user = await api.get<AuthUser>('/users/me');
    setState((s) => ({ ...s, user }));
  }

  async function login(email: string, password: string) {
    const res = await api.post<{
      user: AuthUser;
      tokens: { access_token: string; refresh_token: string };
    }>('/auth/login', { email, password });
    await setTokens(res.tokens.access_token, res.tokens.refresh_token);
  }

  /**
   * 🔴 086 (gap-list F10): signing out clears **every** credential, not just the person's own.
   *
   * Before this, `impersonation_token` and the stored context survived sign-out — and `api.ts:41`
   * resolves `impersonation_token ?? access_token`, preferring it **whenever present**. So an
   * administrator who signed out while acting as a renter was not signed out: every later request
   * went as the renter, and `AuthContext`'s own mount effect (which prefers the same token,
   * correctly) signed them back in as the renter on the next load.
   *
   * The keys are removed **by name** rather than by wiping storage, because 080 writes the sign-in
   * return path immediately before calling this — a blanket clear would delete that feature from
   * inside the one path it exists for. `AuthContext.logout — every key this file writes` pins the
   * list against this file's own `setItem` calls, so a key added later fails loudly here.
   */
  function logout() {
    const rt = localStorage.getItem('refresh_token');
    if (rt) api.post('/auth/logout', { refresh_token: rt }).catch(() => {});

    // 🔴 Before the storage is cleared, never after. `/auth/impersonate/stop` identifies the session
    // from the credential on the request (`auth/routes.ts:183-186`), so clearing first turns this
    // into a guaranteed 404 — which the `.catch` would swallow, leaving the session `active` and
    // 409ing the next impersonation while looking like it worked. Only sent when there is one to
    // end, so an ordinary sign-out issues no request certain to fail.
    if (localStorage.getItem(IMP_TOKEN_KEY)) {
      api.post('/auth/impersonate/stop').catch(() => {});
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(IMP_TOKEN_KEY);
    localStorage.removeItem(IMP_CONTEXT_KEY);
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      loading: false,
      isImpersonating: false,
      impersonation: null,
    });
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setTokens,
        refreshUser,
        enterImpersonation,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
